type AtlasKind = "egg" | "enemy" | "weapon" | "item" | "interface";

interface AtlasDefinition {
  readonly src: string;
  readonly columns: number;
  readonly rows: number;
  readonly ids: readonly string[];
  readonly image?: HTMLImageElement;
}

const EGG_SPRITE_IDS = [
  "fresh-egg",
  "hard-boiled",
  "runner-egg",
  "scramble-egg",
  "poached-egg",
  "giant-egg",
  "spicy-egg",
  "lucky-egg",
  "royal-egg",
  "magnet-egg",
  "double-yolk",
  "sunny-egg",
] as const;

const ENEMY_SPRITE_IDS = [
  "crumb",
  "mite",
  "scuttler",
  "brute",
  "roller",
  "stinger",
  "slime",
  "moth",
  "charger",
  "shellback",
  "red-captain",
  "void-cook",
  "iron-maw",
  "jam-queen",
  "clockwork-chef",
  "the-breakfast",
] as const;

const WEAPON_SPRITE_IDS = [
  "shell-pistol",
  "fork",
  "yolk-blaster",
  "whisk",
  "eggbeater",
  "toast-launcher",
  "pepper-shaker",
  "frying-pan",
  "butter-knife",
  "hot-sauce",
  "freezer-pop",
  "rolling-pin",
  "coffee-sprayer",
  "soda-popper",
  "magnet-coil",
  "egg-timer",
  "rubber-spatula",
  "salt-cannon",
  "blender-blade",
  "toaster-arc",
  "jam-jar",
  "milk-wand",
  "breakfast-laser",
  "golden-ladle",
] as const;

const ITEM_SPRITE_IDS = [
  "tiny-carton", "running-shoes", "paper-plate", "hot-pepper",
  "alarm-clock", "long-straw", "bouncy-napkin", "warm-mug",
  "small-spring", "sturdy-cup", "sugar-cube", "ketchup-packet",
  "oven-mitt", "protein-toast", "double-espresso", "sharp-grater",
  "roller-skate", "vacuum-lid", "lucky-spoon", "soup-thermos",
  "compressed-air", "lunch-box", "energy-jam", "rubber-sole",
  "cast-iron-lid", "giant-carton", "triple-shot", "chef-knife",
  "rocket-apron", "tablecloth-net", "four-leaf-toast", "slow-cooker",
  "pressure-nozzle", "fortified-bowl", "breakfast-combo", "greased-tray",
  "titanium-shell", "midnight-coffee", "dragon-sauce", "jet-skates",
  "black-hole-cup", "loaded-dice", "eternal-kettle", "rail-nozzle",
  "royal-platter", "sunrise-special", "miracle-butter", "perfect-breakfast",
] as const;

const INTERFACE_SPRITE_IDS = [
  "thicker-shell", "quick-feet", "hard-boiled", "spicy-yolk", "rapid-crack", "strong-throw",
  "wide-reach", "lucky-roll", "shell-mend", "battle-breakfast", "morning-rush", "ceramic-coat",
  "sharp-prongs", "steady-aim", "spiced-flame", "critical-crack", "siphon-straw", "long-reach",
  "lucky-break", "morning-harvest", "kitchen-engineer", "firm-grip",
  "crate-normal", "crate-legendary", "material", "consumable", "tree", "health",
  "experience", "timer", "wave", "shop", "recycle", "lock", "achievement", "danger",
] as const;

function loadImage(src: string): HTMLImageElement | undefined {
  if (typeof Image === "undefined") return undefined;
  const image = new Image();
  image.decoding = "async";
  image.src = src;
  return image;
}

function atlas(
  src: string,
  columns: number,
  rows: number,
  ids: readonly string[],
  preloadForCanvas = false,
): AtlasDefinition {
  return {
    src,
    columns,
    rows,
    ids,
    image: preloadForCanvas ? loadImage(src) : undefined,
  };
}

const ATLASES: Readonly<Record<AtlasKind, AtlasDefinition>> = {
  egg: atlas("/assets/generated/egg-heroes.png", 4, 3, EGG_SPRITE_IDS, true),
  enemy: atlas("/assets/generated/enemy-roster.png", 4, 4, ENEMY_SPRITE_IDS, true),
  weapon: atlas("/assets/generated/weapon-icons.png", 6, 4, WEAPON_SPRITE_IDS, true),
  item: atlas("/assets/generated/item-icons.png", 8, 6, ITEM_SPRITE_IDS),
  interface: atlas(
    "/assets/generated/interface-icons.svg",
    6,
    6,
    INTERFACE_SPRITE_IDS,
    true,
  ),
};

export const GENERATED_ASSET_IDS = {
  eggs: EGG_SPRITE_IDS,
  enemies: ENEMY_SPRITE_IDS,
  weapons: WEAPON_SPRITE_IDS,
  items: ITEM_SPRITE_IDS,
  interface: INTERFACE_SPRITE_IDS,
} as const;

export function spriteMarkup(
  kind: AtlasKind,
  id: string,
  className = "",
): string {
  const definition = ATLASES[kind];
  const index = definition.ids.indexOf(id);
  if (index < 0) return "";
  const column = index % definition.columns;
  const row = Math.floor(index / definition.columns);
  const x = definition.columns === 1 ? 0 : (column / (definition.columns - 1)) * 100;
  const y = definition.rows === 1 ? 0 : (row / (definition.rows - 1)) * 100;
  return `<span class="asset-sprite ${kind}-sprite ${className}" style="--sprite-x:${x}%;--sprite-y:${y}%" aria-hidden="true"></span>`;
}

export function drawAtlasSprite(
  context: CanvasRenderingContext2D,
  kind: "egg" | "enemy" | "weapon" | "interface",
  id: string,
  centerX: number,
  centerY: number,
  width: number,
  height = width,
): boolean {
  const definition = ATLASES[kind];
  const image = definition.image;
  const index = definition.ids.indexOf(id);
  if (!image?.complete || image.naturalWidth === 0 || index < 0) return false;
  const sourceWidth = image.naturalWidth / definition.columns;
  const sourceHeight = image.naturalHeight / definition.rows;
  const column = index % definition.columns;
  const row = Math.floor(index / definition.columns);
  context.drawImage(
    image,
    column * sourceWidth,
    row * sourceHeight,
    sourceWidth,
    sourceHeight,
    centerX - width / 2,
    centerY - height / 2,
    width,
    height,
  );
  return true;
}

const arenaImage = loadImage("/assets/generated/breakfast-arena.png");

export function drawArenaAsset(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): boolean {
  if (!arenaImage?.complete || arenaImage.naturalWidth === 0) return false;
  context.drawImage(arenaImage, 0, 0, width, height);
  return true;
}
